// viewRotation — キャンバス表示回転の純粋ロジック (canvas-rotation 段取り 2、
// docs/_wip/canvas-rotation-2026-10-03.md)。
//
// 回転は表示だけの transient 状態 (protocol / engine / snapshot 不触)。ここは
//   - 角度の正規化 / 最短差 / 2 点のなす角
//   - 0/90/180/270° への吸着
//   - 「ひねり量がしきい値を超えるまで回転を始めない」トラッカー
// だけを持ち、DOM / React に依存しない (= vitest node env でテスト対象)。
// 回転の幾何 (どの点を軸に回すか・clamp) は `viewport.ts` §1c。
//
// 角度は度、時計回り正 (CSS `rotate()` と同じ、画面座標は y 下向き)。

/**
 * タッチ: 2 本指のひねり量がこの角度 (度) を超えるまで回転を始めない。ピンチ・パン中の
 * 指のぶれで意図せず傾くのを防ぐ。超えた時点を 0° 起点に滑らかに回り始める (ジャンプしない)。
 * 実機で調整する前提の仮値 (consult 要検証)。
 */
export const ROTATION_GESTURE_THRESHOLD_DEG = 12;

/**
 * PC のドラッグ回転のしきい値。R ツール / Shift+Space は回転する意図が明確なので 0
 * (= すぐ回り始める)。
 */
export const ROTATION_DRAG_THRESHOLD_DEG = 0;

/** 0/90/180/270° の各吸着点から ±この角度 (度) 以内で吸い付く (タッチ・PC 共通)。 */
export const ROTATION_SNAP_DEG = 6;

/** 左回転 / 右回転コマンド (registry `view.rotateLeft` / `view.rotateRight`) の 1 回の角度。 */
export const ROTATION_STEP_DEG = 15;

/** 角度を `[0, 360)` に正規化する。NaN / 非有限は 0。`-0` は出さない。 */
export const normalizeRotation = (deg: number): number => {
  if (!Number.isFinite(deg)) return 0;
  const r = ((deg % 360) + 360) % 360;
  return r === 0 ? 0 : r;
};

/** `from` → `to` の最短の符号付き差 (度)、`(-180, 180]`。 */
export const shortestAngleDelta = (from: number, to: number): number => {
  let d = (to - from) % 360;
  if (d > 180) d -= 360;
  else if (d <= -180) d += 360;
  return d;
};

/** 点 a → 点 b のベクトルの角度 (度、画面座標で時計回り正、`(-180, 180]`)。 */
export const vectorAngleDeg = (ax: number, ay: number, bx: number, by: number): number =>
  (Math.atan2(by - ay, bx - ax) * 180) / Math.PI;

/**
 * 0/90/180/270° のいずれかから `snapDeg` 以内なら、その角度に吸着させて返す。
 * 返り値は常に `[0, 360)` に正規化済み (360 → 0)。
 */
export const snapRotation = (deg: number, snapDeg: number = ROTATION_SNAP_DEG): number => {
  const n = normalizeRotation(deg);
  if (!(snapDeg > 0)) return n;
  const nearest = Math.round(n / 90) * 90;
  if (Math.abs(n - nearest) <= snapDeg) return normalizeRotation(nearest);
  return n;
};

export interface RotationTracker {
  /**
   * 現在の角度 (2 本指ベクトル / 枠中央から見たポインタ方向、度) を渡す。
   * まだしきい値以内で回転を始めないなら `null`、始めたら新しい回転量 (吸着済み、`[0,360)`)。
   */
  update(angleDeg: number): number | null;
}

export interface RotationTrackerOptions {
  /** ジェスチャ開始時点の表示回転 (度)。 */
  readonly startRotation: number;
  /** ジェスチャ開始時点の入力角度 (度)。 */
  readonly startAngle: number;
  /** しきい値 (度)。0 以下なら最初から回る。 */
  readonly thresholdDeg: number;
  /** 吸着幅 (度)。0 以下なら吸着しない。 */
  readonly snapDeg: number;
}

/**
 * ひねり (入力角度の累積変化) → 表示回転の状態機械。
 *
 * - 角度は ±180° の折り返しをまたいでも累積できるよう、フレーム間の最短差を積算する。
 * - 累積が `thresholdDeg` に達するまでは `null` (= 回転しない)。達した後は
 *   「しきい値ぶんを引いた量」を足すので、回り始めに角度がジャンプしない。
 *   一度回り始めたら (しきい値内へ戻しても) 回転は続く。
 * - 結果は吸着 (`snapRotation`) してから返す。
 * - 開始回転は絶対値で保持するため、フレーム毎の誤差は蓄積しない。
 */
export const createRotationTracker = (opts: RotationTrackerOptions): RotationTracker => {
  let lastAngle = opts.startAngle;
  let cumulative = 0;
  let engaged = opts.thresholdDeg <= 0;
  let sign = 0;
  return {
    update(angleDeg: number): number | null {
      if (!Number.isFinite(angleDeg)) return null;
      cumulative += shortestAngleDelta(lastAngle, angleDeg);
      lastAngle = angleDeg;
      if (!engaged) {
        if (Math.abs(cumulative) < opts.thresholdDeg) return null;
        engaged = true;
        sign = cumulative > 0 ? 1 : -1;
      }
      const effective = cumulative - sign * Math.max(0, opts.thresholdDeg);
      return snapRotation(opts.startRotation + effective, opts.snapDeg);
    },
  };
};
